import { useMemo, useState, useEffect, useRef, type ReactNode } from "react";
import heroImage from "@/assets/hero-cmyk.jpg";

import { ProductCarousel } from "@/components/ProductCarousel";
import { Button } from "@/components/ui/button";
import { Headphones, Truck, ChevronDown, Lock, Tag } from "lucide-react";

// Simple reveal-on-view wrapper: slides from right on first view
const RevealRight = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'} transform-gpu transition-all duration-700 ease-out`}
    >
      {children}
    </div>
  );
};

const Index = () => {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const bgStyle = useMemo(() => ({
    background: `radial-gradient(600px circle at ${pos.x}% ${pos.y}%, hsl(var(--brand-cyan) / 0.15), transparent 60%)`
  }), [pos]);

  const handleMove = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x, y });
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Mon Copieur",
    url: "/",
    logo: "/lovable-uploads/d5b011bc-34ae-449a-8ec6-8e13ea03ceae.png",
    sameAs: []
  };

  // Preload hero poster image for faster LCP
  useEffect(() => {
    const id = 'preload-hero-poster';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'preload';
      link.as = 'image';
      link.href = heroImage;
      (link as any).fetchPriority = 'high';
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
        <main>
        <section aria-label="Héros vidéo" className="relative h-screen overflow-hidden border-b border-border" onMouseMove={handleMove}>
          <video
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={heroImage}
            aria-label="Présentation vidéo d'imprimantes couleur CMYK"
            style={{ fetchPriority: 'high' } as any}
          >
            <source src="https://media.moncopieur.ma/hero.webm" type="video/webm" />
            <source src="https://media.moncopieur.ma/hero.mp4" type="video/mp4" />
          </video>

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-brand-cyan/20 via-brand-magenta/10 to-brand-yellow/10" />
          <div className="pointer-events-none absolute inset-0" style={bgStyle} />

          <h1 className="sr-only">Mon Copieur – Imprimantes et consommables</h1>
          <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
            <Button
              asChild
              variant="outline"
              size="icon"
              aria-label="Voir la suite"
              className="rounded-full bg-background/60 backdrop-blur hover:bg-background/80"
            >
              <a href="#guarantees">
                <ChevronDown className="h-5 w-5" />
              </a>
            </Button>
          </div>
        </section>

        <section id="guarantees" aria-label="Garanties" className="border-b border-border bg-secondary/40">
          <div className="container mx-auto px-4 py-8">
            <ul className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <li className="group relative overflow-hidden rounded-xl border border-border bg-card/60 p-6">
                <span
                  className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-primary origin-bottom-right scale-0 transform transition-transform duration-500 ease-out group-hover:scale-110"
                  aria-hidden="true"
                />
                <RevealRight delay={0}>
                  <div className="relative z-10 flex flex-col items-center justify-center text-center gap-3 transition-colors duration-500">
                    <span className="rounded-2xl p-3 text-primary transition-colors duration-500 group-hover:text-primary-foreground"><Lock className="h-10 w-10 md:h-12 md:w-12" /></span>
                    <span className="text-sm font-semibold tracking-wide transition-colors duration-500 group-hover:text-primary-foreground">PAIEMENTS SÉCURISÉS</span>
                  </div>
                </RevealRight>
              </li>
              <li className="group relative overflow-hidden rounded-xl border border-border bg-card/60 p-6">
                <span
                  className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-primary origin-bottom-right scale-0 transform transition-transform duration-500 ease-out group-hover:scale-110"
                  aria-hidden="true"
                />
                <RevealRight delay={100}>
                  <div className="relative z-10 flex flex-col items-center justify-center text-center gap-3 transition-colors duration-500">
                    <span className="rounded-2xl p-3 text-primary transition-colors duration-500 group-hover:text-primary-foreground"><Tag className="h-10 w-10 md:h-12 md:w-12" /></span>
                    <span className="text-sm font-semibold tracking-wide transition-colors duration-500 group-hover:text-primary-foreground">PRIX BAS GARANTIS</span>
                  </div>
                </RevealRight>
              </li>
              <li className="group relative overflow-hidden rounded-xl border border-border bg-card/60 p-6">
                <span
                  className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-primary origin-bottom-right scale-0 transform transition-transform duration-500 ease-out group-hover:scale-110"
                  aria-hidden="true"
                />
                <RevealRight delay={200}>
                  <div className="relative z-10 flex flex-col items-center justify-center text-center gap-3 transition-colors duration-500">
                    <span className="rounded-2xl p-3 text-primary transition-colors duration-500 group-hover:text-primary-foreground"><Headphones className="h-10 w-10 md:h-12 md:w-12" /></span>
                    <span className="text-sm font-semibold tracking-wide transition-colors duration-500 group-hover:text-primary-foreground">CONSEILS ET DEVIS</span>
                  </div>
                </RevealRight>
              </li>
              <li className="group relative overflow-hidden rounded-xl border border-border bg-card/60 p-6">
                <span
                  className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-primary origin-bottom-right scale-0 transform transition-transform duration-500 ease-out group-hover:scale-110"
                  aria-hidden="true"
                />
                <RevealRight delay={300}>
                  <div className="relative z-10 flex flex-col items-center justify-center text-center gap-3 transition-colors duration-500">
                    <span className="rounded-2xl p-3 text-primary transition-colors duration-500 group-hover:text-primary-foreground"><Truck className="h-10 w-10 md:h-12 md:w-12" /></span>
                    <span className="text-sm font-semibold tracking-wide transition-colors duration-500 group-hover:text-primary-foreground">LIVRAISON EXPRESS</span>
                  </div>
                </RevealRight>
              </li>
            </ul>
          </div>
        </section>

        <ProductCarousel
          title="Meilleures ventes"
          description="Les produits les plus populaires chez nos clients"
          type="top-sold"
          limit={8}
        />

        <ProductCarousel
          title="Nouveautés"
          description="Découvrez nos derniers produits"
          type="newest"
          limit={8}
        />

        <section id="contact" className="container mx-auto px-4 py-16">
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <h3 className="font-heading text-2xl font-semibold">Besoin d'un devis ou d'un conseil ?</h3>
            <p className="mt-2 text-muted-foreground">Contactez-nous, nous répondons rapidement.</p>
            <div className="mt-6">
              <Button asChild variant="default" size="lg">
                <a href="/contact">Nous écrire</a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </div>
  );
};

export default Index;